// AUTO-GENERATED from packages/core-schemas/src/dreamwright_core_schemas/api_contracts.py
// Do not edit manually. Run: uv run python scripts/gen_variant_marker_types.py

// RFC-1146: per-variant favourite/dislike markers.
//
// Markers are project-scoped and SHARED across collaborators, and are
// DISPLAY-ONLY — they never affect the node's picked variant or outputs.
//
// NEUTRAL is not a value in this union: it is represented by the ABSENCE of a
// marker (no row server-side, no map entry client-side). The 3-state UI cycle
// is therefore: undefined -> 'favorite' -> 'disliked' -> undefined.

export const VARIANT_MARKER_VALUES: string[] = ['favorite', 'disliked'];

export const VARIANT_MARKER_LOCAL_PATH_MAX_LEN: number = 512;
export const VARIANT_MARKER_NODE_ID_MAX_LEN: number = 256;

export type VariantMarker = 'favorite' | 'disliked';

export interface VariantMarkerEntry {
  node_id: string;
  local_path: string;
  marker: VariantMarker;
  updated_by: string;
  updated_at: string;
}

export interface VariantMarkersResponse {
  project_id: string;
  node_id: string | null;
  markers: VariantMarkerEntry[];
}

// ---------------------------------------------------------------------------
// Runtime validation (schema boundary — no `as` casts per CLAUDE.md #3)
// ---------------------------------------------------------------------------

export type ApiValue =
  | string
  | number
  | boolean
  | null
  | ApiValue[]
  | { [key: string]: ApiValue };

export interface ApiRecord {
  [key: string]: ApiValue;
}

export class VariantMarkerParseError extends Error {
  readonly field: string;
  readonly expected: string;
  readonly actual: string;
  constructor(field: string, expected: string, actual: string) {
    super(`variant-marker response field '${field}': expected ${expected}, got ${actual}`);
    this.name = 'VariantMarkerParseError';
    this.field = field;
    this.expected = expected;
    this.actual = actual;
  }
}

function _typeOf(value: ApiValue | undefined): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function isApiRecord(value: ApiValue | null | undefined): value is ApiRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isApiValueArray(value: ApiValue | null | undefined): value is ApiValue[] {
  return Array.isArray(value);
}

function _fieldValue(record: ApiRecord, key: string): ApiValue | undefined {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

function _record(raw: ApiValue | null | undefined, field: string): ApiRecord {
  if (!isApiRecord(raw)) {
    throw new VariantMarkerParseError(field, 'object', _typeOf(raw));
  }
  return raw;
}

// Required non-nullable fields deliberately have NO `?? default` fallback:
// backend drift must surface loudly at the API boundary rather than being
// coerced into a valid-looking value (CLAUDE.md principle 2).
function _string(raw: ApiValue | undefined, field: string): string {
  if (typeof raw !== 'string') {
    throw new VariantMarkerParseError(field, 'string', _typeOf(raw));
  }
  return raw;
}

function _stringOrNull(raw: ApiValue | undefined, field: string): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') {
    throw new VariantMarkerParseError(field, 'string|null', _typeOf(raw));
  }
  return raw;
}

export function parseVariantMarker(
  raw: ApiValue | undefined,
  field: string,
): VariantMarker {
  const value = _string(raw, field);
  for (const allowed of VARIANT_MARKER_VALUES) {
    if (value === allowed) {
      // Narrow the validated string back to the union. The loop proves
      // membership; TS cannot infer that from a string[] so we re-test the
      // literals explicitly rather than cast.
      if (value === 'favorite') return 'favorite';
      if (value === 'disliked') return 'disliked';
    }
  }
  throw new VariantMarkerParseError(field, 'VariantMarker', 'string');
}

export function parseVariantMarkerEntry(
  raw: ApiValue | null | undefined,
): VariantMarkerEntry {
  const obj = _record(raw, 'marker_entry');
  return {
    node_id: _string(_fieldValue(obj, 'node_id'), 'marker_entry.node_id'),
    local_path: _string(_fieldValue(obj, 'local_path'), 'marker_entry.local_path'),
    marker: parseVariantMarker(_fieldValue(obj, 'marker'), 'marker_entry.marker'),
    updated_by: _string(_fieldValue(obj, 'updated_by'), 'marker_entry.updated_by'),
    updated_at: _string(_fieldValue(obj, 'updated_at'), 'marker_entry.updated_at'),
  };
}

export function parseVariantMarkersResponse(
  raw: ApiValue | null | undefined,
): VariantMarkersResponse {
  const obj = _record(raw, 'response');
  const rawMarkers = _fieldValue(obj, 'markers');
  if (!isApiValueArray(rawMarkers)) {
    throw new VariantMarkerParseError('response.markers', 'array', _typeOf(rawMarkers));
  }
  const markers: VariantMarkerEntry[] = [];
  for (let i = 0; i < rawMarkers.length; i++) {
    markers.push(parseVariantMarkerEntry(rawMarkers[i]));
  }
  return {
    project_id: _string(_fieldValue(obj, 'project_id'), 'response.project_id'),
    node_id: _stringOrNull(_fieldValue(obj, 'node_id'), 'response.node_id'),
    markers,
  };
}

/**
 * Build the client-side marker map keyed by the variant's `local_path`.
 *
 * Absence from this map means NEUTRAL.
 */
export function markersByLocalPath(
  response: VariantMarkersResponse,
): Map<string, VariantMarker> {
  const map = new Map<string, VariantMarker>();
  for (const entry of response.markers) {
    map.set(entry.local_path, entry.marker);
  }
  return map;
}
